import { LOAN_CONTRACT, formatDate, formatDollars } from '@utils';

type LoanDetailsType = {
  title: string;
  items: {
    symbol?: string;
    label: string;
    value?: string | number;
    description?: string;
  }[];
}[];

export const useApplicationDetails = ({ application, quote }): LoanDetailsType => {
  const {
    FINANCIAL_TABLE,
    LOAN_CONTRACT_TABLE_FIELDS,
    FEES,
    FIRST_PAYMENT_DELAYS,
    ESTABLISHMENT_FEE_SUB,
    WEEKLY_ACCOUNT_KEEPING_FEE_SUB,
    LATE_PAYMENT_FEE_SUB,
    INTRODUCER_FEE_SUB,
  } = LOAN_CONTRACT;

  if (!application || !quote) return [];

  const { repaymentTerm, approved, productConfiguration, financedAmount } = application;
  const { interestRate, firstPaymentDelayDays, establishmentFee, weeklyAccountFee, latePaymentFee, introducerFee } =
    productConfiguration;

  const { termInMonths, paymentCycle, amount, totalInterest, totalPayments, termNumberOfPayments } = repaymentTerm;

  const format = (value: number) =>
    formatDollars(value, {
      hideZeroCents: false,
      zeroValue: '$0.00',
    });

  if (!application || !quote) return [];

  return [
    {
      title: FINANCIAL_TABLE,
      items: [
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.dated,
          value: approved ? formatDate(approved) : '',
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.loan,
          value: format(financedAmount),
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.annualPercentageRate,
          value: `${interestRate}%`,
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.totalInterestPayable,
          value: format(totalInterest),
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.totalMinimumAmountPayable,
          value: format(totalPayments),
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.noOfFortnights,
          value: termNumberOfPayments,
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.firstRepaymentDate,
          value: FIRST_PAYMENT_DELAYS(firstPaymentDelayDays),
        },
      ],
    },
    {
      title: FEES,
      items: [
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.establishmentFee,
          value: format(establishmentFee),
          description: ESTABLISHMENT_FEE_SUB,
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.weeklyAccountKeepingFee,
          value: `${format(weeklyAccountFee)} per week`,
          description: WEEKLY_ACCOUNT_KEEPING_FEE_SUB(
            formatDollars((weeklyAccountFee ?? 0) * 2, { zeroValue: '$0.00', hideZeroCents: false }),
          ),
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.latePaymentFee,
          value: format(latePaymentFee),
          description: LATE_PAYMENT_FEE_SUB,
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.introducerFee,
          value: format(introducerFee),
          description: INTRODUCER_FEE_SUB,
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.totalFees,
          // calculate total fees
          value: format(totalPayments - totalInterest - financedAmount),
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.amountToPay({
            term: parseInt(termInMonths) / 12,
            paymentCycle: paymentCycle.replace('ly', ''),
          }),
          value: format(amount),
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.repaymentChanges,
        },
        {
          label: LOAN_CONTRACT_TABLE_FIELDS.commissionAmount,
        },
      ],
    },
  ];
};
